import React from "react";
import { Link } from "react-router-dom";
import { useSelector } from "react-redux";
import { ReactComponent as SearchNotFound } from "../../../assets/icons/searchnotfound.svg";

// TIP-556 — when a search can't surface the user's company, we offer the two
// recovery paths everywhere with the same links:
//   1. Claim the profile for free — the existing sign-up claim wizard
//      (redirectToStep 6, same pattern as ProfileBanner / BusinessProfileWidget;
//      the wizard itself enforces login/registration before a claim request).
//   2. Order a web profile — the /order-web-profile purchase form, pre-filled
//      with the failed search (company name + country) via router state.

export const useOrderWebProfilePrefill = () => {
  const { searchQuery, countryData } = useSelector((state) => state.search);
  return { prefill: { companyName: searchQuery, countryCode: countryData?.code } };
};

export const ClaimOrOrderActions = ({ claimLabel = "Claim your business. It's free.", orderLabel = "Order a web profile" }) => {
  const orderState = useOrderWebProfilePrefill();
  return (
    <div className="flex flex-col sm:flex-row gap-3 justify-center">
      <Link
        to="/sign-up"
        state={{ redirectToStep: 6 }}
        className="bg-primary text-white font-sans font-semibold text-f14 rounded-lg px-6 py-3 text-center"
      >
        {claimLabel}
      </Link>
      <Link
        to="/order-web-profile"
        state={orderState}
        className="border border-primary text-primary font-sans font-semibold text-f14 rounded-lg px-6 py-3 text-center"
      >
        {orderLabel}
      </Link>
    </div>
  );
};

// Reference-design empty state for the main /search page: dashed card, icon,
// `No businesses match "<query>".`, helper text, and the two CTAs.
export const SearchEmptyState = ({ heading }) => {
  const { searchQuery } = useSelector((state) => state.search);
  return (
    <div className="w-full mt-5 p-10 sm:p-16 flex flex-col items-center gap-3 border border-dashed border-secondary rounded-lg text-center">
      <div className="p-4 rounded-2xl bg-orange-50">
        <SearchNotFound />
      </div>
      <h3 className="text-f24 font-bold text-primary font-sans">
        {heading ?? `No businesses match "${searchQuery}".`}
      </h3>
      <p className="text-secondary text-f16 font-sans max-w-xl">
        Try a different spelling. If the business is not yet on TREVEX, claim
        its profile for free, or order a web profile and we will build it.
      </p>
      <div className="mt-3">
        <ClaimOrOrderActions />
      </div>
    </div>
  );
};

// Shown under the LAST page of results — the user scrolled everything and
// still didn't find their company.
export const ResultsFootActions = () => (
  <div className="w-full mt-6 p-6 flex flex-col items-center gap-3 border border-dashed border-secondary rounded-lg text-center">
    <p className="text-primary text-f16 font-semibold font-sans">
      Don't see your business?
    </p>
    <ClaimOrOrderActions claimLabel="Claim your business for free" />
  </div>
);
